import React, { useState, useCallback, useRef } from 'react'
import { connect } from 'react-redux'
import { AgGridReact } from 'ag-grid-react'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import { DatePicker, Button, message } from 'antd'
import moment from 'moment'
import { UtilityUnitSelection } from '../../core'
import { Title, CriteriaRow, Content } from 'core/screen-util'
import {
  getApiTargetInfo,
  getEventTypes,
  getContribOperCondition,
  getCauseCodeData,
} from '../../core/selectors'
import {
  getAeciHourlyEvents,
  saveAeciHourlyEvents,
  deleteAeciHourlyEvents,
} from '../aeciApi'
import { DATE_FORMAT_API } from 'enum'
import EventModal from './EventModal'

const EVENTSTART_FORMAT = 'MM/DD/YYYY HH:mm'

const columnDefs = [
  {
    headerName: '',
    checkboxSelection: true,
    headerCheckboxSelection: true,
    width: 40,
    pinned: 'left',
    sortable: false,
    filter: false,
  },
  { headerName: 'Event #', field: 'eventNum', width: 80, editable: false },
  { headerName: 'Event Type', field: 'eventType', width: 110, editable: false },
  {
    headerName: 'Event Start',
    field: 'eventStart',
    width: 150,
    editable: false,
  },
  { headerName: 'Zone', field: 'eventStartZone', width: 70, editable: false },
  { headerName: 'Avail Cap', field: 'availCap', width: 90, editable: false },
  { headerName: 'Cause Code', field: 'causeCode', width: 100, editable: false },
  { headerName: 'COC', field: 'coc', width: 70, editable: false },
  {
    headerName: 'Description',
    field: 'description',
    width: 220,
    editable: false,
  },
  { headerName: 'Submitted', field: 'isSubmitted', width: 90, editable: false },
  {
    headerName: 'Modifier',
    field: 'modifier',
    width: 110,
    editable: false,
    valueGetter: p => p.data.modificationInfo?.modifier,
  },
  {
    headerName: 'Modified',
    field: 'modifiedDate',
    width: 130,
    editable: false,
    valueGetter: p => p.data.modificationInfo?.modifiedDate,
  },
]

const defaultColDef = { resizable: true, sortable: true, filter: true }

const HourlyEvent = ({
  apiTargetInfo,
  eventTypes,
  contribConditions,
  causeCodeList,
}) => {
  const [selectedDate, setSelectedDate] = useState(moment())
  const [selectedUtility, setSelectedUtility] = useState(null)
  const [selectedUnit, setSelectedUnit] = useState(null)
  const [rowData, setRowData] = useState([])
  const [loading, setLoading] = useState(false)

  // modal state
  const [modalVisible, setModalVisible] = useState(false)
  const [editData, setEditData] = useState(null) // null = add, object = edit
  const [editRowKey, setEditRowKey] = useState(null)

  const gridRef = useRef()
  const unitId = selectedUnit

  const handleRetrieve = useCallback(() => {
    if (!unitId) return
    setLoading(true)
    getAeciHourlyEvents(apiTargetInfo)({
      unitId,
      date: selectedDate.format(DATE_FORMAT_API),
    }).fork(
      () => {
        message.error('Failed to retrieve events')
        setLoading(false)
      },
      data => {
        setRowData(
          data.map(e => ({
            ...e,
            eventStart: e.eventStart
              ? moment(e.eventStart).format(EVENTSTART_FORMAT)
              : '',
          }))
        )
        setLoading(false)
      }
    )
  }, [apiTargetInfo, unitId, selectedDate])

  // open modal for new row
  const handleAdd = useCallback(() => {
    setEditData(null)
    setEditRowKey(null)
    setModalVisible(true)
  }, [])

  // open modal when a row is double-clicked
  const onRowDoubleClicked = useCallback(({ data }) => {
    setEditData(data)
    setEditRowKey(
      data.utility && data.unit && data.eventType && data.eventStart
        ? `${data.utility}${data.unit}-${data.eventType}-${data.eventStart}-${data.causeCode}-${data.eventNum}`
        : null
    )
    setModalVisible(true)
  }, [])

  const toSavePayload = useCallback(
    rows =>
      rows.map(r => ({
        utility: r.utility || selectedUtility,
        unit: r.unit || selectedUnit,
        eventNum: r.eventNum ?? -1,
        eventType: r.eventType,
        eventStart: r.eventStart,
        availCap: r.availCap || null,
        causeCode: r.causeCode || 0,
        coc: r.coc || null,
        description: r.description || null,
        isSubmitted: r.isSubmitted || null,
      })),
    [selectedUtility, selectedUnit]
  )

  // called by modal on OK
  const handleModalSave = useCallback(
    values => {
      const baseRow = {
        utility: selectedUtility,
        unit: selectedUnit ? selectedUnit.substr(3) : selectedUnit,
        eventNum: editData?.eventNum ?? -1,
        eventType: values.eventType,
        eventStart: values.eventStart,
        availCap: values.availCap ?? null,
        causeCode: values.causeCode || 0,
        coc: values.coc || null,
        description: values.description || null,
        isSubmitted: values.isSubmitted || null,
        eventStartZone: editData?.eventStartZone || '',
        modificationInfo: editData?.modificationInfo || null,
      }

      const allRows = [baseRow]

      if (editRowKey) {
        setRowData(prev =>
          prev.map(r => {
            const key =
              r.utility && r.unit && r.eventType && r.eventStart
                ? `${r.utility}${r.unit}-${r.eventType}-${r.eventStart}-${r.causeCode}-${r.eventNum}`
                : null
            return key === editRowKey ? baseRow : r
          })
        )
      } else {
        setRowData(prev => [...allRows, ...prev])
      }

      setModalVisible(false)

      // immediately persist
      setLoading(true)
      saveAeciHourlyEvents(apiTargetInfo)(toSavePayload(allRows)).fork(
        () => {
          message.error('Save failed')
          setLoading(false)
        },
        () => {
          message.success('Saved')
          handleRetrieve()
        }
      )
    },
    [
      apiTargetInfo,
      selectedUtility,
      selectedUnit,
      editData,
      editRowKey,
      toSavePayload,
      handleRetrieve,
    ]
  )

  const getSelectedRows = useCallback(() => {
    if (!gridRef.current) return []
    return gridRef.current.api.getSelectedNodes().map(n => n.data)
  }, [])

  const handleSave = useCallback(() => {
    const rows = getSelectedRows()
    if (!rows.length) {
      message.warning('Select rows to save')
      return
    }
    setLoading(true)
    saveAeciHourlyEvents(apiTargetInfo)(toSavePayload(rows)).fork(
      () => {
        message.error('Save failed')
        setLoading(false)
      },
      () => {
        message.success('Saved')
        handleRetrieve()
      }
    )
  }, [apiTargetInfo, getSelectedRows, toSavePayload, handleRetrieve])

  const handleDelete = useCallback(() => {
    const rows = getSelectedRows()
    if (!rows.length) {
      message.warning('Select rows to delete')
      return
    }
    setLoading(true)
    deleteAeciHourlyEvents(apiTargetInfo)(toSavePayload(rows)).fork(
      () => {
        message.error('Delete failed')
        setLoading(false)
      },
      () => {
        message.success('Deleted')
        handleRetrieve()
      }
    )
  }, [apiTargetInfo, getSelectedRows, toSavePayload, handleRetrieve])

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CriteriaRow>
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Title>AECI Hourly Events</Title>

          <span>Date:</span>
          <DatePicker
            value={selectedDate}
            onChange={setSelectedDate}
            allowClear={false}
            format="MMM/DD/YYYY"
          />

          <span>Unit:</span>
          <UtilityUnitSelection
            utility={selectedUtility}
            unit={selectedUnit}
            casStyle={{ width: 280 }}
            onUnitChange={val => {
              if (val && val.length === 2) {
                setSelectedUtility(val[0])
                setSelectedUnit(val[1])
              }
            }}
          />

          <Button
            type="primary"
            icon="search"
            onClick={handleRetrieve}
            disabled={!unitId}
            loading={loading}
          >
            Retrieve
          </Button>

          <Button icon="plus" onClick={handleAdd} disabled={!unitId}>
            Add
          </Button>

          <Button icon="save" onClick={handleSave} disabled={!unitId}>
            Save
          </Button>

          <Button
            icon="delete"
            type="danger"
            onClick={handleDelete}
            disabled={!unitId}
          >
            Delete
          </Button>
        </div>
      </CriteriaRow>

      <Content style={{ flex: 1, padding: 10 }}>
        <div
          className="ag-theme-alpine"
          style={{ height: '80vh', width: '100%' }}
        >
          <AgGridReact
            ref={gridRef}
            columnDefs={columnDefs}
            rowData={rowData}
            defaultColDef={defaultColDef}
            rowSelection="multiple"
            suppressRowClickSelection
            onRowDoubleClicked={onRowDoubleClicked}
            getRowId={p =>
              p.data.utility &&
              p.data.unit &&
              p.data.eventType &&
              p.data.eventStart
                ? `${p.data.utility}${p.data.unit}-${p.data.eventType}-${p.data.eventStart}-${p.data.causeCode}-${p.data.eventNum}`
                : `new-${Math.random()}`
            }
          />
        </div>
      </Content>

      <EventModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onSave={handleModalSave}
        editData={editData}
        selectedDate={selectedDate}
        existingEvents={editData ? [] : rowData}
        selectedUnit={unitId}
        eventTypes={eventTypes}
        contribConditions={contribConditions}
        causeCodeList={causeCodeList}
      />
    </div>
  )
}

const mapStateToProps = state => ({
  apiTargetInfo: getApiTargetInfo(state),
  eventTypes: getEventTypes(state),
  contribConditions: getContribOperCondition(state),
  causeCodeList: getCauseCodeData(state),
})

export default connect(mapStateToProps)(HourlyEvent)
